import { execSQL, esc, num } from './api';

const INC_SP   = 'dbo.payroll_increment_sp';
const BONUS_SP = 'dbo.payroll_bonus_sp';

/* ===================== INCREMENTS =====================
 * The client's flow, from the 6 August meeting: in the increment month the
 * rise is shown as its own line on the payslip; from the following month it
 * is simply part of basic salary. So an increment has two steps:
 *
 *   Draft   — amounts being typed in
 *   Applied — payroll for that month picks it up as an "Increment" line,
 *             basic salary is untouched
 *   Merged  — folded into basic salary, done once that month is paid
 * ====================================================== */

/* Budgetary allowance rides along so the sheet reads the way Nadee's figures
 * do - basic plus budgetary is the salary everyone quotes. It is read from the
 * salary structure for display only; the increment itself still only moves basic. */
export const fetchIncrements = (month) =>
  execSQL(`
    SELECT i.*,
           -- shown the payslip way; the name stored when the sheet was generated used the first name
           ISNULL(NULLIF(LTRIM(RTRIM(ISNULL(e.initial,'') + ISNULL(e.initials,'') + ' ' + ISNULL(e.surName,''))), ''), i.empName) AS displayName,
           ISNULL((SELECT SUM(s.value) FROM dbo.payroll_salary_structure s
                   WHERE s.emp_id = i.emp_id AND s.status <> 'Deleted' AND s.type = 'Allowance'
                     AND s.componentName LIKE '%budgetary%'), 0) AS budgetaryAllowance
    FROM dbo.payroll_increment i
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = i.emp_id
    WHERE i.effectiveMonth = ${esc(month)}
    ORDER BY CAST(i.emp_id AS INT)
  `);

export const clearIncrementDraft = (month) =>
  execSQL(`EXEC ${INC_SP} @action='CLEAR',@effectiveMonth=${esc(month)}`);

export const updateIncrement = (id, amount) =>
  execSQL(`EXEC ${INC_SP} @action='UPDATE',@id=${num(id)},@incrementAmount=${num(amount)}`);

export const applyIncrements = (month) =>
  execSQL(`EXEC ${INC_SP} @action='APPLY',@effectiveMonth=${esc(month)}`);

// Folds the applied increments into basic salary — run once the increment
// month has been paid, so the following month starts from the new basic.
export const mergeIncrementsIntoBasic = (month) =>
  execSQL(`EXEC ${INC_SP} @action='MERGE',@effectiveMonth=${esc(month)}`);

/* Taking a step back. Both return one row - affected, skipped, blocked - so the
 * screen can say exactly what happened.
 *   REVOKE  - Applied back to Draft, amounts kept, and the zero rows APPLY
 *             cleared away are put back so the sheet is whole again. Refused
 *             once that month's payroll is marked Paid.
 *   UNMERGE - Merged back to Applied, basic salary returned to its old figure
 *             for anyone whose basic has not been edited since. Refused once a
 *             later month has been paid on the new basic. */
export const revokeIncrements = (month) =>
  execSQL(`EXEC ${INC_SP} @action='REVOKE',@effectiveMonth=${esc(month)}`);

export const undoIncrementMerge = (month) =>
  execSQL(`EXEC ${INC_SP} @action='UNMERGE',@effectiveMonth=${esc(month)}`);

// Increments that should appear as their own line in a given payroll month.
export const fetchIncrementsForMonth = (month) =>
  execSQL(`EXEC dbo.payroll_increment_for_month_sp @runMonth=${esc(month)}`);

// Seeds a draft row for every active employee at their current basic.
export const seedIncrementSheet = async (month, employees, batchSize = 25) => {
  for (let i = 0; i < employees.length; i += batchSize) {
    const stmts = employees.slice(i, i + batchSize).map((e) => {
      const basic = Number(e.basicSalary) || 0;
      return `EXEC ${INC_SP} @action='INSERT',@effectiveMonth=${esc(month)},` +
             `@emp_id=${esc(e.emp_id)},@empName=${esc(e.empName)},@category=${esc(e.category)},` +
             `@currentBasic=${basic},@incrementAmount=0,@newBasic=${basic}`;
    });
    await execSQL(stmts.join('\n'));
  }
};

/* ======================= BONUS ========================
 * Client rule: one month's salary = Basic + Budgetary Allowance,
 * normally paid in December. Amounts stay editable per employee.
 * ====================================================== */

export const fetchBonuses = (month) =>
  execSQL(`
    SELECT * FROM dbo.payroll_bonus
    WHERE bonusMonth = ${esc(month)}
    ORDER BY CAST(emp_id AS INT)
  `);

export const clearBonusDraft = (month) =>
  execSQL(`EXEC ${BONUS_SP} @action='CLEAR',@bonusMonth=${esc(month)}`);

export const updateBonus = (id, amount) =>
  execSQL(`EXEC ${BONUS_SP} @action='UPDATE',@id=${num(id)},@bonusAmount=${num(amount)}`);

export const finaliseBonuses = (month) =>
  execSQL(`EXEC ${BONUS_SP} @action='FINALISE',@bonusMonth=${esc(month)}`);

export const seedBonusSheet = async (month, rows, batchSize = 25) => {
  for (let i = 0; i < rows.length; i += batchSize) {
    const stmts = rows.slice(i, i + batchSize).map((r) =>
      `EXEC ${BONUS_SP} @action='INSERT',@bonusMonth=${esc(month)},` +
      `@emp_id=${esc(r.emp_id)},@empName=${esc(r.empName)},@category=${esc(r.category)},` +
      `@basicSalary=${num(r.basicSalary)},@budgetaryAllowance=${num(r.budgetaryAllowance)},` +
      `@bonusAmount=${num(r.bonusAmount)}`
    );
    await execSQL(stmts.join('\n'));
  }
};
